import shaka from 'shaka-player';
import { PlayerRequestType, PlayerError, KeySystem } from '@dtvgo/player-common-utils';

const { util: { Error: ShakaError }, net: { NetworkingEngine: { RequestType } } } = shaka;
const ERROR_NAMES_BY_CODE = invertEnum(ShakaError.Code);
const MAP_KEY_SYSTEM_TO_PROP = invertEnum(KeySystem);
function invertEnum(enumeration) {
  return Object.fromEntries(Object.entries(enumeration).map(([name, code]) => [code, name]));
}
const RequestTypeMapping = {
  [RequestType.MANIFEST]: PlayerRequestType.MANIFEST,
  [RequestType.SEGMENT]: PlayerRequestType.SEGMENT,
  [RequestType.LICENSE]: PlayerRequestType.LICENSE,
  [RequestType.APP]: PlayerRequestType.APP,
  [RequestType.TIMING]: PlayerRequestType.TIMING,
  [RequestType.SERVER_CERTIFICATE]: PlayerRequestType.SERVER_CERTIFICATE,
  [RequestType.KEY]: PlayerRequestType.KEY,
  [RequestType.ADS]: PlayerRequestType.ADS,
  other: PlayerRequestType.OTHER
};
const SeverityMapping = {
  [ShakaError.Severity.CRITICAL]: PlayerError.Severity.CRITICAL,
  [ShakaError.Severity.RECOVERABLE]: PlayerError.Severity.NON_CRITICAL
};
const requestTypeTransform = {
  name: "requestType",
  transform: (shakaRequestType) => RequestTypeMapping[shakaRequestType] || RequestTypeMapping.other
};
const keySystemTransform = {
  name: "keySystem",
  transform: (keySystem) => MAP_KEY_SYSTEM_TO_PROP[keySystem] || keySystem
};
const SHAKA_ERROR_RENORMALIZE = "SHAKA_ERROR_RENORMALIZE";
const ErrorMappingPerCategory = {
  [ShakaError.Category.NETWORK]: {
    code: PlayerError.Code.NETWORK_OTHER
  },
  [ShakaError.Category.TEXT]: {
    code: PlayerError.Code.INVALID_SEGMENT
  },
  [ShakaError.Category.MEDIA]: {
    code: PlayerError.Code.INVALID_SEGMENT
  },
  [ShakaError.Category.MANIFEST]: {
    code: PlayerError.Code.INVALID_MANIFEST
  },
  [ShakaError.Category.STREAMING]: {
    code: PlayerError.Code.OTHER
  },
  [ShakaError.Category.DRM]: {
    code: PlayerError.Code.DRM_OTHER
  },
  [ShakaError.Category.PLAYER]: {
    code: PlayerError.Code.OTHER
  },
  [ShakaError.Category.CAST]: {
    code: PlayerError.Code.OTHER
  },
  [ShakaError.Category.STORAGE]: {
    code: PlayerError.Code.OTHER
  },
  [ShakaError.Category.ADS]: {
    code: PlayerError.Code.OTHER
  }
};
const ErrorMappingPerCode = {
  [ShakaError.Code.UNSUPPORTED_SCHEME]: {
    code: PlayerError.Code.MALFORMED_DATA_URI,
    data: ["uri"]
  },
  [ShakaError.Code.BAD_HTTP_STATUS]: {
    code: PlayerError.Code.BAD_HTTP_STATUS,
    data: ["uri", "responseCode", "serverResponse", "responseHeaders", requestTypeTransform, "finalUri"]
  },
  [ShakaError.Code.HTTP_ERROR]: {
    code: PlayerError.Code.HTTP_ERROR,
    data: ["uri", "originalError", requestTypeTransform]
  },
  [ShakaError.Code.TIMEOUT]: {
    code: PlayerError.Code.TIMEOUT,
    data: ["uri", requestTypeTransform]
  },
  [ShakaError.Code.MALFORMED_DATA_URI]: {
    code: PlayerError.Code.MALFORMED_DATA_URI,
    data: ["uri"]
  },
  [ShakaError.Code.REQUEST_FILTER_ERROR]: {
    code: PlayerError.Code.FILTER_ERROR,
    data: ["originalError"]
  },
  [ShakaError.Code.RESPONSE_FILTER_ERROR]: {
    code: PlayerError.Code.FILTER_ERROR,
    data: ["originalError"]
  },
  [ShakaError.Code.SEGMENT_MISSING]: {
    code: PlayerError.Code.SEGMENT_MISSING,
    data: ["uri"]
  },
  [ShakaError.Code.INVALID_TEXT_CUE]: {
    code: void 0,
    data: ["extraContext"]
  },
  [ShakaError.Code.INVALID_XML]: {
    code: void 0,
    data: ["extraContext"]
  },
  [ShakaError.Code.UNABLE_TO_EXTRACT_CUE_START_TIME]: {
    code: void 0,
    data: ["originalError"]
  },
  [ShakaError.Code.TEXT_COULD_NOT_GUESS_MIME_TYPE]: {
    code: void 0,
    data: ["uri"]
  },
  [ShakaError.Code.CANNOT_ADD_EXTERNAL_TEXT_TO_SRC_EQUALS]: {
    code: PlayerError.Code.STREAMING_UNSUPPORTED,
    data: []
  },
  [ShakaError.Code.TEXT_ONLY_WEBVTT_SRC_EQUALS]: {
    code: PlayerError.Code.STREAMING_UNSUPPORTED,
    data: ["mimeType"]
  },
  [ShakaError.Code.MISSING_TEXT_PLUGIN]: {
    code: PlayerError.Code.MISSING_TEXT_PLUGIN,
    data: ["mimeType"]
  },
  [ShakaError.Code.CHAPTERS_TRACK_FAILED]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: []
  },
  [ShakaError.Code.UNSUPPORTED_EXTERNAL_THUMBNAILS_URI]: {
    code: PlayerError.Code.STREAMING_UNSUPPORTED,
    data: ["uri"]
  },
  [ShakaError.Code.BUFFER_READ_OUT_OF_BOUNDS]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: []
  },
  [ShakaError.Code.JS_INTEGER_OVERFLOW]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: []
  },
  [ShakaError.Code.EBML_OVERFLOW]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: []
  },
  [ShakaError.Code.EBML_BAD_FLOATING_POINT_SIZE]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: []
  },
  [ShakaError.Code.MEDIA_SOURCE_OPERATION_FAILED]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: ["mediaErrorCode"]
  },
  [ShakaError.Code.MEDIA_SOURCE_OPERATION_THREW]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: ["originalError", "mediaError"]
  },
  [ShakaError.Code.VIDEO_ERROR]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: ["mediaErrorCode", "microsoftExtendedErrorCode", "errorDetails"]
  },
  [ShakaError.Code.QUOTA_EXCEEDED_ERROR]: {
    code: PlayerError.Code.STREAMING_UNSUPPORTED,
    data: ["contentType"]
  },
  [ShakaError.Code.TRANSMUXING_FAILED]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: ["uri"]
  },
  [ShakaError.Code.CONTENT_TRANSFORMATION_FAILED]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: ["uri"]
  },
  [ShakaError.Code.MSS_MISSING_DATA_FOR_TRANSMUXING]: {
    code: void 0,
    data: ["uri"]
  },
  [ShakaError.Code.MSS_TRANSMUXING_FAILED]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: ["uri"]
  },
  [ShakaError.Code.TRANSMUXING_NO_VIDEO_DATA]: {
    code: PlayerError.Code.STREAMING_ERROR,
    data: ["uri"]
  },
  [ShakaError.Code.UNABLE_TO_GUESS_MANIFEST_TYPE]: {
    code: void 0,
    data: ["uri", "mimeType"]
  },
  [ShakaError.Code.DASH_INVALID_XML]: {
    code: void 0,
    data: ["uri"]
  },
  [ShakaError.Code.RESTRICTIONS_CANNOT_BE_MET]: {
    code: (error, data) => {
      if (data?.restrictionInfo?.restrictedKeyStatuses.includes("output-restricted")) {
        return PlayerError.Code.DRM_RESTRICTED_OUTPUT;
      }
      return PlayerError.Code.RESTRICTIONS_CANNOT_BE_MET;
    },
    data: ["restrictionInfo"]
  },
  [ShakaError.Code.INVALID_HLS_TAG]: {
    code: void 0,
    data: ["invalidTag"]
  },
  [ShakaError.Code.HLS_REQUIRED_ATTRIBUTE_MISSING]: {
    code: void 0,
    data: ["missingAttributeName"]
  },
  [ShakaError.Code.HLS_REQUIRED_TAG_MISSING]: {
    code: void 0,
    data: ["missingTagName"]
  },
  [ShakaError.Code.HLS_COULD_NOT_GUESS_CODECS]: {
    code: void 0,
    data: ["variantCodecs"]
  },
  [ShakaError.Code.CONTENT_UNSUPPORTED_BY_BROWSER]: {
    code: PlayerError.Code.STREAMING_UNSUPPORTED,
    data: []
  },
  [ShakaError.Code.PERIOD_FLATTENING_FAILED]: {
    code: PlayerError.Code.PERIOD_FLATTENING_FAILED,
    data: []
  },
  [ShakaError.Code.HLS_VARIABLE_NOT_FOUND]: {
    code: void 0,
    data: ["undeclaredVariable"]
  },
  [ShakaError.Code.NO_WEB_CRYPTO_API]: {
    code: PlayerError.Code.OTHER,
    data: []
  },
  [ShakaError.Code.MSS_INVALID_XML]: {
    code: void 0,
    data: ["uri"]
  },
  [ShakaError.Code.MSS_LIVE_CONTENT_NOT_SUPPORTED]: {
    code: PlayerError.Code.STREAMING_UNSUPPORTED,
    data: []
  },
  [ShakaError.Code.NO_RECOGNIZED_KEY_SYSTEMS]: {
    code: PlayerError.Code.INVALID_KEYSYSTEM,
    data: []
  },
  [ShakaError.Code.REQUESTED_KEY_SYSTEM_CONFIG_UNAVAILABLE]: {
    code: PlayerError.Code.KEYSYSTEM_UNAVAILABLE,
    data: []
  },
  [ShakaError.Code.FAILED_TO_CREATE_CDM]: {
    code: PlayerError.Code.DRM_INITIALIZATION_FAILED,
    data: ["errorMessage"]
  },
  [ShakaError.Code.FAILED_TO_ATTACH_TO_VIDEO]: {
    code: PlayerError.Code.DRM_INITIALIZATION_FAILED,
    data: ["errorMessage"]
  },
  [ShakaError.Code.INVALID_SERVER_CERTIFICATE]: {
    code: PlayerError.Code.INVALID_SERVER_CERTIFICATE,
    data: ["errorMessage"]
  },
  [ShakaError.Code.FAILED_TO_CREATE_SESSION]: {
    code: PlayerError.Code.DRM_INITIALIZATION_FAILED,
    data: ["errorMessage"]
  },
  [ShakaError.Code.FAILED_TO_GENERATE_LICENSE_REQUEST]: {
    code: PlayerError.Code.FAILED_TO_GENERATE_LICENSE_REQUEST,
    data: ["errorMessage", "originalError", "extendedErrorCode"]
  },
  [ShakaError.Code.LICENSE_REQUEST_FAILED]: {
    code: PlayerError.Code.LICENSE_REQUEST_FAILED,
    data: [SHAKA_ERROR_RENORMALIZE]
  },
  [ShakaError.Code.LICENSE_RESPONSE_REJECTED]: {
    code: PlayerError.Code.LICENSE_RESPONSE_REJECTED,
    data: ["errorMessage"]
  },
  [ShakaError.Code.ENCRYPTED_CONTENT_WITHOUT_DRM_INFO]: {
    code: PlayerError.Code.INVALID_MANIFEST,
    data: []
  },
  [ShakaError.Code.NO_LICENSE_SERVER_GIVEN]: {
    code: PlayerError.Code.DRM_INVALID_CONFIG,
    data: [keySystemTransform]
  },
  [ShakaError.Code.OFFLINE_SESSION_REMOVED]: {
    code: PlayerError.Code.OFFLINE_SESSION_REMOVED,
    data: []
  },
  [ShakaError.Code.EXPIRED]: {
    code: PlayerError.Code.LICENSE_EXPIRED,
    data: []
  },
  [ShakaError.Code.SERVER_CERTIFICATE_REQUIRED]: {
    code: PlayerError.Code.DRM_INVALID_CONFIG,
    data: []
  },
  [ShakaError.Code.INIT_DATA_TRANSFORM_ERROR]: {
    code: PlayerError.Code.DRM_INITIALIZATION_FAILED,
    data: ["originalError"]
  },
  [ShakaError.Code.SERVER_CERTIFICATE_REQUEST_FAILED]: {
    code: PlayerError.Code.SERVER_CERTIFICATE_REQUEST_FAILED,
    data: [SHAKA_ERROR_RENORMALIZE]
  },
  [ShakaError.Code.MIN_HDCP_VERSION_NOT_MATCH]: {
    code: PlayerError.Code.DRM_RESTRICTED_OUTPUT,
    data: []
  },
  [ShakaError.Code.ERROR_CHECKING_HDCP_VERSION]: {
    code: void 0,
    data: ["errorMessage"]
  },
  [ShakaError.Code.LOAD_INTERRUPTED]: {
    code: PlayerError.Code.LOAD_INTERRUPTED,
    data: []
  },
  [ShakaError.Code.OPERATION_ABORTED]: {
    code: PlayerError.Code.OPERATION_ABORTED,
    data: ["uri", requestTypeTransform]
  },
  [ShakaError.Code.NO_VIDEO_ELEMENT]: {
    code: PlayerError.Code.BAD_PLAYER_INITIALIZATION,
    data: []
  },
  [ShakaError.Code.OBJECT_DESTROYED]: {
    code: PlayerError.Code.OBJECT_DESTROYED,
    data: []
  },
  [ShakaError.Code.CONTENT_NOT_LOADED]: {
    code: PlayerError.Code.BAD_PLAYER_INITIALIZATION,
    data: []
  }
};
function getErrorAttributes(shakaError) {
  if (!shakaError) {
    return {};
  }
  const codeMapping = ErrorMappingPerCode[shakaError.code];
  const categoryMapping = ErrorMappingPerCategory[shakaError.category];
  const data = {};
  const dataMapping = codeMapping?.data || [];
  for (const [index, dataTransform] of dataMapping.entries()) {
    if (dataTransform === SHAKA_ERROR_RENORMALIZE) {
      const {
        code: internalErrorCode,
        data: internalData
      } = getErrorAttributes(shakaError.data?.[index]);
      Object.assign(data, internalData, { internalErrorCode });
    }
    if (typeof dataTransform === "string") {
      data[dataTransform] = shakaError.data?.[index];
    }
    if (typeof dataTransform === "object" && dataTransform.name && dataTransform.transform) {
      data[dataTransform.name] = dataTransform.transform(shakaError.data?.[index]);
    }
  }
  let code = codeMapping?.code || categoryMapping?.code;
  if (typeof code === "function") {
    code = code(shakaError, data);
  }
  return { data, code };
}
function normalizeError(shakaError) {
  const { code, data } = getErrorAttributes(shakaError);
  let severity = SeverityMapping[shakaError.severity];
  if (shakaError.severity === ShakaError.Severity.CRITICAL) {
    if (shakaError.ignored)
      severity = PlayerError.Severity.NON_CRITICAL;
    else if (shakaError.handled)
      severity = PlayerError.Severity.HANDLED;
  }
  return PlayerError.create({
    severity,
    code,
    playerCode: `${shakaError.code} - ${ERROR_NAMES_BY_CODE[shakaError.code]}`,
    data,
    originalError: shakaError
  });
}

export { ERROR_NAMES_BY_CODE, MAP_KEY_SYSTEM_TO_PROP, RequestTypeMapping, getErrorAttributes, invertEnum, normalizeError };
//# sourceMappingURL=errorNormalizer.js.map
